/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useCallback, useEffect } from 'react';
import { token } from '@atlaskit/tokens';
import { useExpandSideNav } from '@atlaskit/navigation-system/layout/side-nav';
import { expVal } from '@atlassian/jira-feature-experiments';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import {
	type UIAnalyticsEvent,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { JiraSpotlight } from '@atlassian/jira-spotlight/src/ui/jira-spotlight.tsx';
import { useRouterActions } from '@atlassian/react-resource-router';
import type { SpotlightProps } from '@atlassian/jira-spotlight/src/ui/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { ConditionalSpotlightTransition } from '../../../ConditionalSpotlightTransition.tsx';
import { ROADMAP_INTRODUCTION_SPOTLIGHT } from '../../../../controllers/constants.tsx';
import { useOnboardingFlowsActions } from '../../../../controllers/index.tsx';
import {
	useCollectionsIntroductionDismissed,
	useCollectionsIntroductionPrerequisiteDismissalsMet,
} from '../../../../controllers/selectors/collections-hooks.tsx';
import { useProductUsageStartTimestamp } from '../../../../controllers/selectors/product-usage-start-timestamp-hooks.tsx';
import { canMigrateToNewSpotlightPackage } from '../../../../common/canMigrateToNewSpotlightPackage.tsx';
import { checkIfOlderThanNDays } from '../../../../utils/checkIfOlderThanNDays.tsx';
import { useIsLicensedUser } from './utils.tsx';
import { useHasAtLeastTwoJpdSpaces } from './useHasAtLeastTwoJpdSpaces.tsx';

const dismissMessage = defineMessage({
	id: 'polaris-lib-onboarding-flows.collections.tour.introduction.dismiss',
	defaultMessage: 'Dismiss',
	description: 'Dismiss button for the tour spotlight',
});

const giveItATry = defineMessage({
	id: 'polaris-lib-onboarding-flows.collections.tour.introduction.give-it-a-try',
	defaultMessage: 'Give it a try!',
	description: 'The button to take user to the roadmaps view',
});

const message = defineMessage({
	id: 'polaris-lib-onboarding-flows.collections.tour.introduction.message',
	defaultMessage:
		'Create a roadmap tailored to the information your stakeholders care about the most by selecting which projects and ideas to include.',
	description: 'Text that notifies the user of the new feature',
});

const messageGalaxia = defineMessage({
	id: 'polaris-lib-onboarding-flows.collections.tour.introduction.message-galaxia',
	defaultMessage:
		'Create a roadmap tailored to the information your stakeholders care about the most by selecting which spaces and ideas to include.',
	description: 'Text that notifies the user of the new feature',
});

const heading = defineMessage({
	id: 'polaris-lib-onboarding-flows.collections.tour.roadmap-introduction-spotlight.heading',
	defaultMessage: 'Build executive roadmaps',
	description: 'Heading for the roadmap introduction spotlight',
});

const messageNew = defineMessage({
	id: 'polaris-lib-onboarding-flows.collections.tour.roadmap-introduction-spotlight.message',
	defaultMessage:
		'Choose which projects and ideas to include so your stakeholders see what matters most.',
	description: 'Text that notifies the user of the new feature',
});

const messageNewGalaxia = defineMessage({
	id: 'polaris-lib-onboarding-flows.collections.tour.roadmap-introduction-spotlight.message-galaxia',
	defaultMessage:
		'Choose which spaces and ideas to include so your stakeholders see what matters most.',
	description: 'Text that notifies the user of the new feature',
});

export const checkIfIsAlreadyInRoadmaps = (): boolean => {
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	return window?.location.href.includes('roadmap');
};

export const RoadmapIntroductionSpotlight = ({
	collectionsUrl,
	placement,
}: {
	collectionsUrl: string;
	placement?: SpotlightProps['dialogPlacement'];
}): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const isLicensedUser = useIsLicensedUser();
	const { push } = useRouterActions();
	const expandSideNav = useExpandSideNav();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isAlreadyInRoadmaps = checkIfIsAlreadyInRoadmaps();

	const {
		loadRoadmapIntroductionDismissed,
		loadRoadmapIntroductionPrerequisiteDismissalsMet,
		loadProductUsageStartTimestamp,
		setRoadmapIntroductionDismissed,
	} = useOnboardingFlowsActions();

	useEffect(() => {
		// we can prevent unnecessary calls in these cases
		if (isAlreadyInRoadmaps || !isLicensedUser) {
			return;
		}
		loadRoadmapIntroductionDismissed();
		loadRoadmapIntroductionPrerequisiteDismissalsMet();
		loadProductUsageStartTimestamp();
	}, [
		isAlreadyInRoadmaps,
		isLicensedUser,
		loadProductUsageStartTimestamp,
		loadRoadmapIntroductionDismissed,
		loadRoadmapIntroductionPrerequisiteDismissalsMet,
	]);

	const isIntroductionDismissed = useCollectionsIntroductionDismissed();
	const arePrerequisiteDismissalsMet = useCollectionsIntroductionPrerequisiteDismissalsMet();
	const productUsageStartTimestamp = useProductUsageStartTimestamp();
	const hasAtLeastTwoSpaces = useHasAtLeastTwoJpdSpaces();

	const dismiss = useCallback(
		(analyticsEvent: UIAnalyticsEvent) => {
			if (fg('pol-21470-juno-spotlights-migration')) {
				fireUIAnalytics(analyticsEvent, { activeSpotlight: ROADMAP_INTRODUCTION_SPOTLIGHT });
			} else {
				fireUIAnalytics(analyticsEvent, 'dismiss');
			}
			setRoadmapIntroductionDismissed();
		},
		[setRoadmapIntroductionDismissed],
	);

	const onCloseButtonDismiss = useCallback(() => {
		dismiss(
			createAnalyticsEvent({
				action: 'dismissed',
				actionSubject: 'jpdSpotlight',
			}),
		);
	}, [dismiss, createAnalyticsEvent]);

	if (
		isAlreadyInRoadmaps ||
		!isLicensedUser ||
		isIntroductionDismissed === undefined ||
		isIntroductionDismissed ||
		!arePrerequisiteDismissalsMet ||
		!checkIfOlderThanNDays(productUsageStartTimestamp) ||
		!hasAtLeastTwoSpaces
	) {
		return null;
	}

	const openRoadmaps = (analyticsEvent?: UIAnalyticsEvent) => {
		if (analyticsEvent) {
			fireUIAnalytics(analyticsEvent, 'openRoadmaps');
		} else {
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'button',
				}),
				'openRoadmaps',
			);
		}
		setRoadmapIntroductionDismissed();
		push(collectionsUrl);
	};

	expandSideNav();

	const getOldMessage = () => {
		return formatMessage(
			expVal('project-terminology-refresh', 'isEnabled', false) ? messageGalaxia : message,
		);
	};

	const getNewMessage = () => {
		return formatMessage(
			expVal('project-terminology-refresh', 'isEnabled', false) ? messageNewGalaxia : messageNew,
		);
	};

	if (!fg('pol-21470-juno-spotlights-migration')) {
		return (
			<ConditionalSpotlightTransition>
				<JiraSpotlight
					actions={[
						{
							onClick: (_: unknown, analyticsEvent: UIAnalyticsEvent) => dismiss(analyticsEvent),
							text: formatMessage(dismissMessage),
							testId: 'roadmap-introduction-spotlight-dismiss-button',
						},
						{
							onClick: (_: unknown, analyticsEvent: UIAnalyticsEvent) =>
								openRoadmaps(analyticsEvent),
							text: formatMessage(giveItATry),
						},
					]}
					dialogPlacement={placement}
					dialogWidth={275}
					target={ROADMAP_INTRODUCTION_SPOTLIGHT}
					key={ROADMAP_INTRODUCTION_SPOTLIGHT}
					targetRadius={3}
					targetBgColor={token('elevation.surface')}
					targetOnClick={(_event) => openRoadmaps()}
					// eslint-disable-next-line jira/choreographer/message-id-by-folder-structure
					messageId="polaris-lib-onboarding-flows.ui.collections.tour.roadmap-introduction-spotlight.jira-spotlight"
					messageType="engagement"
					isMigrated={canMigrateToNewSpotlightPackage()}
				>
					{getOldMessage()}
				</JiraSpotlight>
			</ConditionalSpotlightTransition>
		);
	}

	return (
		<ConditionalSpotlightTransition>
			<JiraSpotlight
				actions={[
					{
						onClick: (_: unknown, analyticsEvent: UIAnalyticsEvent) => openRoadmaps(analyticsEvent),
						text: formatMessage(giveItATry),
					},
				]}
				dialogPlacement={placement}
				dialogWidth={275}
				heading={formatMessage(heading)}
				target={ROADMAP_INTRODUCTION_SPOTLIGHT}
				key={ROADMAP_INTRODUCTION_SPOTLIGHT}
				targetRadius={3}
				targetBgColor={token('elevation.surface')}
				targetOnClick={(_event) => openRoadmaps()}
				// eslint-disable-next-line jira/choreographer/message-id-by-folder-structure
				messageId="polaris-lib-onboarding-flows.ui.collections.tour.roadmap-introduction-spotlight.jira-spotlight"
				messageType="engagement"
				isMigrated={canMigrateToNewSpotlightPackage()}
				onDismiss={onCloseButtonDismiss}
			>
				{getNewMessage()}
			</JiraSpotlight>
		</ConditionalSpotlightTransition>
	);
};
